/**
 * Connector API — CRUD, discovery, testing, export.
 */
import { apiClient, buildUrl } from './apiClient';
import type { ConnectorConfig, ExportToConnectorResponse, ExportJobAcceptedResponse } from './types';
export type { ConnectorConfig, ExportToConnectorResponse, ExportJobAcceptedResponse } from './types';
import type { CatalogResponse } from '../types/connectorCatalog';

export async function createConnector(payload: {
  connector_type: string;
  name: string;
  direction: 'source' | 'destination';
  config: Record<string, any>;
  project_id?: string;
  tenant_id?: string;
  created_by?: string;
}): Promise<ConnectorConfig> {
  const response = await apiClient.post<ConnectorConfig>(
    buildUrl('/api/v1/connectors'),
    payload
  );
  return response.data;
}

export async function listConnectors(params?: {
  project_id?: string;
  tenant_id?: string;
  direction?: 'source' | 'destination' | 'bidirectional';
  scope?: 'all' | 'project' | 'global';
  limit?: number;
}): Promise<ConnectorConfig[]> {
  const search = new URLSearchParams();
  if (params?.project_id) {
    search.set('project_id', params.project_id);
  }
  if (params?.tenant_id) {
    search.set('tenant_id', params.tenant_id);
  }
  if (params?.direction) {
    search.set('direction', params.direction);
  }
  if (params?.scope) {
    search.set('scope', params.scope);
  }
  if (params?.limit) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString() ? `?${search.toString()}` : '';
  const response = await apiClient.get<{ connectors: ConnectorConfig[]; count: number }>(
    buildUrl(`/api/v1/connectors${query}`)
  );
  return response.data?.connectors ?? [];
}

export async function getConnectorCatalog(): Promise<CatalogResponse> {
  const response = await apiClient.get<CatalogResponse>(
    buildUrl('/api/v1/connector-catalog')
  );
  return response.data;
}

export async function getConnector(connectorId: string): Promise<ConnectorConfig> {
  const response = await apiClient.get<ConnectorConfig>(
    buildUrl(`/api/v1/connectors/${connectorId}`)
  );
  return response.data;
}

export async function updateConnector(
  connectorId: string,
  data: { name: string; config: Record<string, any>; direction?: string; connector_type?: string }
): Promise<ConnectorConfig> {
  const response = await apiClient.put<ConnectorConfig>(
    buildUrl(`/api/v1/connectors/${connectorId}`),
    data
  );
  return response.data;
}

export async function discoverStreams(params: {
  connectorId?: string;
  connectorType?: string;
  config?: Record<string, any>;
}): Promise<{ streams: { name: string; type: string }[]; connector_type: string; is_filesystem: boolean }> {
  const body: Record<string, any> = {};
  if (params.connectorId) body.connector_id = params.connectorId;
  if (params.connectorType) body.connector_type = params.connectorType;
  if (params.config) body.config = params.config;
  const response = await apiClient.post<{
    streams: { name: string; type: string }[];
    connector_type: string;
    is_filesystem: boolean;
  }>(buildUrl('/api/v1/connectors/discover-streams'), body, { timeout: 120_000 });
  return response.data;
}

export async function testConnector(params: {
  connector_type: string;
  config: Record<string, any>;
}): Promise<{ success: boolean; message?: string }> {
  const response = await apiClient.post<{ success: boolean; message?: string }>(
    buildUrl('/api/v1/connectors/test'),
    params,
    { timeout: 120_000 }, // SSH-tunneled connectors need longer than the 30s default
  );
  return response.data;
}

export async function startTransformation(batchId: string): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/loader/${batchId}/load`));
}

export async function importFromConnector(
  sessionId: string,
  connectorId: string,
  tables: string[],
  columnsByTable?: Record<string, string[]>,
  replaceTable?: string,
  replaceTableGroup?: string[],
): Promise<{
  connector_id: string;
  connector_name: string;
  tables_imported: number;
  tables: Array<{ table_name: string; row_count: number; columns: any[] }>;
  replaced?: boolean;
}> {
  const payload: Record<string, any> = {
    connector_id: connectorId,
    tables,
  };
  if (columnsByTable && Object.keys(columnsByTable).length > 0) {
    payload.columns = columnsByTable;
  }
  if (replaceTable) {
    payload.replace_table = replaceTable;
    if (replaceTableGroup && replaceTableGroup.length > 1) {
      payload.replace_tables = replaceTableGroup;
    }
  }
  const response = await apiClient.post<{
    connector_id: string;
    connector_name: string;
    tables_imported: number;
    tables: Array<{ table_name: string; row_count: number; columns: any[] }>;
    replaced?: boolean;
  }>(buildUrl(`/api/v1/agents/sessions/${sessionId}/import-from-connector`), payload);
  return response.data;
}

// ---------------------------------------------------------------------------
// Connector file preview (Excel sheet detection)
// ---------------------------------------------------------------------------

export interface ConnectorFilePreviewSheetResponse {
  sheet_name: string;
  columns: Array<{ name: string; type: string }>;
  rows: any[][];
  row_count: number;
}

export interface ConnectorFilePreviewResponse {
  file_name: string;
  is_multi_sheet: boolean;
  sheets?: ConnectorFilePreviewSheetResponse[];
  columns?: Array<{ name: string; type: string }>;
  rows?: any[][];
}

export async function previewConnectorFile(
  connectorId: string,
  filePath: string,
  limit = 10,
): Promise<ConnectorFilePreviewResponse> {
  const response = await apiClient.post<ConnectorFilePreviewResponse>(
    buildUrl('/api/v1/connectors/preview-connector-file'),
    { connector_id: connectorId, file_path: filePath, limit },
    { timeout: 120_000 },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Enhanced table discovery & preview
// ---------------------------------------------------------------------------

export interface TableColumnInfo {
  name: string;
  dtype: string;
  nullable: boolean;
}

export interface DiscoveredTable {
  name: string;
  schema_name?: string;
  row_count_estimate?: number;
  column_count?: number;
  columns?: TableColumnInfo[];
  // Client-side state for preview
  previewRows?: any[];
  previewLoading?: boolean;
}

export interface DiscoverTablesResponse {
  tables: DiscoveredTable[];
  total_count: number;
  page: number;
  page_size: number;
  connector_type: string;
}

export async function discoverTables(params: {
  connectorId: string;
  search?: string;
  includeColumns?: boolean;
  page?: number;
  pageSize?: number;
}): Promise<DiscoverTablesResponse> {
  const response = await apiClient.post<DiscoverTablesResponse>(
    buildUrl('/api/v1/connectors/discover-tables'),
    {
      connector_id: params.connectorId,
      search: params.search || null,
      include_columns: params.includeColumns ?? false,
      page: params.page ?? 1,
      page_size: params.pageSize ?? 50,
    },
    { timeout: 120_000 }, // SSH-tunneled connectors need longer than the 30s default
  );
  return response.data;
}

export interface PreviewTableResponse {
  columns: TableColumnInfo[];
  rows: any[];
  total_row_count: number | null;
  truncated: boolean;
}

export async function previewTable(params: {
  connectorId: string;
  tableName: string;
  schemaName?: string;
  columns?: string[];
  limit?: number;
}): Promise<PreviewTableResponse> {
  const response = await apiClient.post<PreviewTableResponse>(
    buildUrl('/api/v1/connectors/preview-table'),
    {
      connector_id: params.connectorId,
      table_name: params.tableName,
      schema_name: params.schemaName || null,
      columns: params.columns || null,
      limit: params.limit ?? 25,
    },
    { timeout: 120_000 }, // SSH-tunneled connectors need longer than the 30s default
  );
  return response.data;
}

// ── Pre-export validation ──────────────────────────────────────────

export interface ColumnIssue {
  column: string;
  source_type: string;
  target_type: string;
  severity: 'ok' | 'warning' | 'error';
  message: string;
}

export interface TableValidationResult {
  table: string;
  status: 'pass' | 'warn' | 'fail';
  issues: ColumnIssue[];
}

export async function validateExport(
  connectorId: string,
  tables: Array<{
    source_table: string;
    target_table: string;
    source_columns: Array<{ name: string; dtype: string }>;
  }>,
  sessionId: string,
): Promise<{ results: TableValidationResult[] }> {
  if (!sessionId) {
    throw new Error('validateExport requires a sessionId — open a workspace session for the batch first.');
  }
  const url = buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/validate-export`);
  const response = await apiClient.post<{ results: TableValidationResult[] }>(
    url,
    { connector_id: connectorId, tables },
  );
  return response.data;
}

export async function exportToConnector(
  batchId: string,
  connectorId: string,
  sessionId: string,
  targetTables?: string[],
  columnFilters?: Record<string, string[]>,
): Promise<ExportJobAcceptedResponse> {
  if (!sessionId) {
    throw new Error('exportToConnector requires a sessionId — open a workspace session for the batch first.');
  }
  const payload: Record<string, any> = {
    batch_id: batchId,
    connector_id: connectorId,
  };
  if (targetTables?.length) {
    payload.target_tables = targetTables;
  }
  if (columnFilters && Object.keys(columnFilters).length > 0) {
    payload.column_filters = columnFilters;
  }
  const url = buildUrl(`/api/v1/agents/sessions/${sessionId}/workspace/export-to-connector`);
  const response = await apiClient.post<ExportJobAcceptedResponse>(url, payload);
  return response.data;
}
